import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  BoxProps,
  Divider,
  Icon,
  Link,
  ProgressSpinner,
  SearchInput,
  Text,
  useResponsive,
} from '@bitrise/bitkit';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import { Account } from '@/models/Account';
import useAccounts from '@/hooks/useAccounts';

type WorkSpaceSelectorItemProps = {
  account: Account;
  shouldFocus: boolean;
  onSelect: (selectedAccount: Account) => void;
} & BoxProps;
const WorkspaceSelectorItem = ({ account, shouldFocus, onSelect }: WorkSpaceSelectorItemProps): JSX.Element => {
  const ref = useRef<HTMLDivElement & HTMLAnchorElement>(null);

  useEffect(() => {
    if (shouldFocus) {
      ref.current?.focus();
    }
  }, [shouldFocus]);
  return (
    <Box
      as="button"
      ref={ref}
      display="flex"
      alignItems="center"
      gap="12"
      paddingX={[24, 12]}
      paddingY="8"
      _hover={{ backgroundColor: 'neutral.95' }}
      width="100%"
      _focus={{ backgroundColor: 'neutral.93' }}
      _focusVisible={{ boxShadow: 'none' }}
      onClick={() => onSelect(account)}
    >
      <Avatar size="40" name={account.name} src={account.avatarUrl} variant="workspace" />
      <Text size="3" hasEllipsis>
        {account.name}
      </Text>
    </Box>
  );
};

const AccountsErrorNotification = (): JSX.Element => (
  <Box display="flex" gap="8" padding="16" backgroundColor="red.93" textColor="red.50" alignItems="center">
    <Icon name="ErrorGeneral" width="20px" height="20px" />
    <Text size="2" flexGrow={1}>
      Failed to load Workspaces.
    </Text>
  </Box>
);

type WorkspaceSelectorPopoverProps = {
  onSelect: (selectedAccount: Account) => void;
};
const WorkspaceSelectorPopover = ({ onSelect }: WorkspaceSelectorPopoverProps): JSX.Element => {
  const { account: selectedAccount } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const { data: accounts, isLoading: isAccountsLoading, isError: isAccountsLoadingError, isSuccess } = useAccounts();
  const [searchValue, setSearchValue] = useState('');
  const { isMobile } = useResponsive();

  const inputRef = useRef<HTMLInputElement>(null);
  const [focusId, setFocusId] = useState(-1);

  let filteredAccounts = accounts || [];
  filteredAccounts = filteredAccounts
    .filter(({ slug }) => selectedAccount.slug !== slug)
    .filter(({ name }) => name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()));

  const hasSearchBar = accounts && accounts.length > 6;
  const isLoadedAndNotEmpty = !isAccountsLoading && !isAccountsLoadingError && isSuccess && accounts.length > 0;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!hasSearchBar) {
        return;
      }

      let newFocusId = focusId;

      if (e.code === 'ArrowDown') {
        e.preventDefault();
        newFocusId = focusId === filteredAccounts.length - 1 ? -1 : focusId + 1;
      } else if (e.code === 'ArrowUp') {
        e.preventDefault();
        newFocusId = focusId <= 0 ? -1 : focusId - 1;
      }

      setFocusId(newFocusId);

      if (newFocusId === -1) {
        inputRef.current?.focus();
      }
    },
    [filteredAccounts.length, focusId, hasSearchBar],
  );

  useEffect(() => {
    if (focusId === -1 && !isMobile) inputRef.current?.focus();
  });

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [handleKeyDown]);

  return (
    <>
      {isLoadedAndNotEmpty && (
        <Box display="flex" alignItems="center" gap="16" marginTop={[0, 24]} paddingX="12">
          <>
            <Text fontWeight="bold" size="1" textTransform="uppercase" color="neutral.60" whiteSpace="nowrap">
              Switch to
            </Text>
            <Divider marginRight="16" />
          </>
        </Box>
      )}
      {hasSearchBar && (
        <SearchInput
          inputRef={inputRef}
          placeholder="Filter by Workspace name"
          margin="8"
          onChange={(newSearchValue) => setSearchValue(newSearchValue)}
        />
      )}
      <Box
        flexDirection="column"
        marginTop="4"
        paddingY={[8, 0]}
        maxHeight={hasSearchBar ? ['calc(100vh - 370px)', '300'] : undefined}
        borderTop={hasSearchBar ? '1px solid' : undefined}
        borderColor="neutral.90"
        overflow="auto"
        overscrollBehaviorY="none"
        marginBottom="8"
      >
        {isAccountsLoadingError && <AccountsErrorNotification />}
        {isAccountsLoading && <ProgressSpinner display="block" marginX="auto" marginTop="20" marginBottom="32" />}
        {filteredAccounts.map((account, id) => (
          <WorkspaceSelectorItem
            shouldFocus={focusId === id}
            key={account.slug}
            account={account}
            onSelect={() => onSelect(account)}
          />
        ))}

        {hasSearchBar && filteredAccounts.length === 0 && (
          <Box textColor="neutral.40" textAlign="center" paddingY="24">
            No matching workspaces found.
          </Box>
        )}
      </Box>
      <Divider />
      <Link
        href="/create-workspace"
        display="block"
        _hover={{ backgroundColor: 'neutral.95' }}
        paddingY="12"
        marginY="12"
        textAlign="left"
      >
        <Icon name="PlusCircle" marginLeft={[16, 24]} marginRight="12" />
        Create Workspace
      </Link>
    </>
  );
};

export default WorkspaceSelectorPopover;
